import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Avatar,
  LinearProgress,
  FormControlLabel,
  Switch,
  Divider,
  Autocomplete,
  Paper,
  FormHelperText
} from '@mui/material';
import {
  PhotoCamera,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
  Pets as PetsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { shelterService } from '@services/sheltersService';
import { compressImage, validateImageType, formatFileSize, createImagePreview } from '@utils/imageCompression';
import { SPECIES, getBreedsBySpecies } from '@constants/animalData';

interface ShelterAnimalFormData {
  name: string;
  species: 'dog' | 'cat' | 'bird' | 'rabbit' | 'horse' | 'other'; // Standardizat
  breed: string;
  age_months: number;
  gender: 'male' | 'female'; // Standardizat (renumit din sex)
  size: 'mica' | 'medie' | 'mare';
  color: string;
  description: string;
  status: 'available' | 'reserved' | 'adopted'; // Standardizat
  microchipId: string;
  microchipped: boolean;
  vaccinated: boolean;
  sterilized: boolean;
  special_needs: string;
  compatibility: string[];
  photos: File[];
}

const GENDER_OPTIONS = [
  { value: 'male', label: 'Mascul' },
  { value: 'female', label: 'Femelă' }
];

const SIZE_OPTIONS = [
  { value: 'mica', label: 'Mică' },
  { value: 'medie', label: 'Medie' },
  { value: 'mare', label: 'Mare' }
];

const STATUS_OPTIONS = [
  { value: 'available', label: 'Disponibil' },
  { value: 'reserved', label: 'Rezervat' },
  { value: 'adopted', label: 'Adoptat' }
];

const COMPATIBILITY_OPTIONS = [
  'Copii',
  'Alte animale',
  'Apartament',
  'Curte',
  'Persoane în vârstă',
  'Familii active'
];

const ShelterAnimalForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<ShelterAnimalFormData>({
    name: '',
    species: 'dog', // Standardizat
    breed: '',
    age_months: 0,
    gender: 'male', // Standardizat
    size: 'medie',
    color: '',
    description: '',
    status: 'available', // Standardizat
    microchipId: '',
    microchipped: false,
    vaccinated: false,
    sterilized: false,
    special_needs: '',
    compatibility: [],
    photos: []
  });

  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number[]>([]);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionInfo, setCompressionInfo] = useState<{
    originalSize: number;
    compressedSize: number;
    ratio: number;
  } | null>(null);

  // Microchip validation state
  const [microchipValidated, setMicrochipValidated] = useState(false);
  const [validatingMicrochip, setValidatingMicrochip] = useState(false);
  const [microchipValidationMessage, setMicrochipValidationMessage] = useState('');

  const handleInputChange = (field: keyof ShelterAnimalFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Microchip validation function
  const validateMicrochip = async (microchip: string): Promise<boolean> => {
    if (!microchip || microchip.trim().length < 10) {
      setMicrochipValidationMessage('Microchip-ul trebuie să aibă cel puțin 10 caractere');
      setMicrochipValidated(false);
      return false;
    }
    
    // Simplified validation - just check length
    setMicrochipValidationMessage('Microchip valid');
    setMicrochipValidated(true);
    return true;
  };
  
  const handleMicrochipChange = (value: string) => {
    handleInputChange('microchipId', value);
    setMicrochipValidated(false);
    setMicrochipValidationMessage('');
  };
  
  const handleMicrochipBlur = async () => {
    if (formData.microchipId) {
      await validateMicrochip(formData.microchipId);
    }
  };

  const validateField = (name: string, value: any): string | null => {
    switch (name) {
      case 'name':
        if (!value || value.trim().length < 2) {
          return 'Numele trebuie să aibă cel puțin 2 caractere';
        }
        if (value.trim().length > 100) {
          return 'Numele nu poate depăși 100 de caractere';
        }
        return null;

      case 'species':
        if (!value) {
          return 'Specia este obligatorie';
        }
        return null;

      case 'breed':
        if (!value || value.trim().length < 2) {
          return 'Rasa este obligatorie';
        }
        return null;

      case 'age_months':
        if (value < 0) {
          return 'Vârsta nu poate fi negativă';
        }
        if (value > 300) {
          return 'Vârsta pare neobișnuit de mare';
        }
        return null;

      case 'gender':
        if (!value) {
          return 'Genul este obligatoriu';
        }
        return null;

      case 'size':
        if (!value) {
          return 'Mărimea este obligatorie';
        }
        return null;

      case 'status':
        if (!value) {
          return 'Statusul este obligatoriu';
        }
        return null;

      case 'microchipId':
        if (!value || value.trim().length < 10) {
          return 'Microchip-ul este obligatoriu și trebuie să aibă cel puțin 10 caractere';
        }
        if (value.trim().length > 20) {
          return 'Microchip-ul nu poate depăși 20 de caractere';
        }
        return null;

      default:
        return null;
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    for (const file of files) {
      if (!validateImageType(file)) {
        setError('Tip de fișier nevalid. Folosiți doar JPG, PNG sau WebP.');
        return;
      }

      try {
        setIsCompressing(true);
        const compressionResult = await compressImage(file);
        const preview = compressionResult.preview;
        
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, compressionResult.file]
        }));
        
        setPhotoPreviews(prev => [...prev, preview]);
        setUploadProgress(prev => [...prev, 0]);
        
        setCompressionInfo({
          originalSize: compressionResult.originalSize,
          compressedSize: compressionResult.compressedSize,
          ratio: compressionResult.compressionRatio
        });
      } catch (error) {
        console.error('Error compressing image:', error);
        setError('Eroare la comprimarea imaginii.');
      } finally {
        setIsCompressing(false);
      }
    }
  };

  const removePhoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
    setUploadProgress(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.shelterId) {
      setError('Shelter ID not found');
      return;
    }

    // Validate all fields
    const errors: Record<string, string> = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key as keyof ShelterAnimalFormData]);
      if (error) {
        errors[key] = error;
      }
    });

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Creează mai întâi animalul fără poze
      const animalData = {
        name: formData.name?.trim(),
        species: formData.species,           // Standardizat: 'dog' | 'cat' | 'bird' | 'rabbit' | 'horse' | 'other'
        gender: formData.gender,             // Standardizat: 'male' | 'female'
        size: formData.size,                 // 'mica' | 'medie' | 'mare' conform schemei
        breed: formData.breed?.trim() || null,
        age_months: formData.age_months || null,
        color: formData.color?.trim() || null,
        weight_kg: null, // Nu avem în form
        height_cm: null, // Nu avem în form
        sterilized: !!formData.sterilized,
        vaccinated: !!formData.vaccinated,
        microchipped: !!formData.microchipped,
        chip_number: formData.microchipId?.trim() || null,
        passport_number: null, // Nu avem în form
        dewormed: false, // Nu avem în form
        deparasit_extern: false, // Nu avem în form
        vaccinated_rabies: false, // Nu avem în form
        special_needs: formData.special_needs?.trim() || null,
        description: formData.description?.trim() || null,
        status: formData.status || 'available', // Standardizat: 'available' | 'reserved' | 'adopted'
        adoptable: true, // Default
        intake_date: null, // Nu avem în form
        adoption_date: null,
        adopted_by_user_id: null
      };

      // Creează animalul
      const createdAnimal = await shelterService.createShelterAnimal(animalData);
      
      // Upload pozele dacă există
      console.log('FormData photos:', formData.photos);
      console.log('Photos length:', formData.photos.length);
      if (formData.photos.length > 0) {
        try {
          console.log('Uploading photos for animal:', createdAnimal.id);
          const uploadResult = await shelterService.uploadAnimalPhotos(createdAnimal.id, formData.photos);
          console.log('Upload result:', uploadResult);
        } catch (photoError) {
          console.error('Error uploading photos:', photoError);
          // Nu oprește procesul dacă upload-ul pozelor eșuează
        }
      } else {
        console.log('No photos to upload');
      }
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/shelter/animals');
      }, 2000);
      
    } catch (error: any) {
      console.error('Error creating animal:', error);
      setError(error.response?.data?.error || 'Eroare la crearea animalului');
    } finally {
      setSaving(false);
    }
  };

  // Get breeds for selected species
  const availableBreeds = formData.species ? getBreedsBySpecies(formData.species) : [];

  if (success) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <PetsIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Animal adăugat cu succes!
            </Typography>
            <Typography color="text.secondary">
              Animalul a fost adăugat în adapost. Veți fi redirecționat înapoi la lista de animale.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/shelter/animals')}
          sx={{ mb: 2 }}
        >
          Înapoi la animale
        </Button>
        
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Adaugă Animal Nou
        </Typography>
        <Typography color="text.secondary">
          Completează informațiile despre animalul pe care vrei să îl adaugi în adapost.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Informații de bază
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Microchip ID - First field with validation (exactly like client form) */}
              <Box>
                <TextField
                  fullWidth
                  required
                  name="microchipId"
                  label="Microchip ID *"
                  value={formData.microchipId || ''}
                  onChange={(e) => handleMicrochipChange(e.target.value)}
                  onBlur={handleMicrochipBlur}
                  error={!!formErrors.microchipId || (!!formData.microchipId && !microchipValidated)}
                  helperText={
                    formErrors.microchipId || 
                    microchipValidationMessage || 
                    'Numărul microchipului (10-20 cifre, obligatoriu)'
                  }
                  disabled={saving || validatingMicrochip}
                  InputProps={{
                    endAdornment: validatingMicrochip ? (
                      <CircularProgress size={20} />
                    ) : microchipValidated ? (
                      <CheckCircleIcon color="success" />
                    ) : null
                  }}
                />
                
                {/* Validation status chip */}
                {formData.microchipId && (
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      icon={microchipValidated ? <CheckCircleIcon /> : <ErrorIcon />}
                      label={microchipValidated ? 'Microchip valid' : 'Microchip invalid'}
                      color={microchipValidated ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                )}
              </Box>

              {/* Rest of fields - disabled until microchip is validated */}
              <Box sx={{ opacity: microchipValidated ? 1 : 0.6 }}>
              {/* Image preview and upload - similar to client form */}
              <Box>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                  {/* Afișează toate pozele cu preview-uri locale */}
                  {photoPreviews.length > 0 ? (
                    <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
                      {photoPreviews.map((preview, index) => (
                        <Box key={index} position="relative">
                          <Avatar
                            src={preview}
                            sx={{ 
                              width: 120, 
                              height: 120, 
                              bgcolor: 'primary.main',
                              border: '2px solid',
                              borderColor: 'primary.main'
                            }}
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removePhoto(index)}
                            disabled={saving || isCompressing || !microchipValidated}
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              bgcolor: 'error.main',
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'error.dark'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Avatar
                      sx={{ width: 120, height: 120, bgcolor: 'primary.main' }}
                    >
                      {formData.name?.charAt(0)?.toUpperCase() || '🐾'}
                    </Avatar>
                  )}

                  {isCompressing && (
                    <Box sx={{ width: '100%', maxWidth: 300 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Se comprimă imaginea...
                      </Typography>
                      <LinearProgress />
                    </Box>
                  )}

                  {compressionInfo && (
                    <Box textAlign="center">
                      <Chip
                        icon={<CheckCircleIcon />}
                        label={`Comprimat: ${formatFileSize(compressionInfo.originalSize)} → ${formatFileSize(compressionInfo.compressedSize)} (${compressionInfo.ratio}% reducere)`}
                        color="success"
                        size="small"
                      />
                    </Box>
                  )}

                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<PhotoCamera />}
                      disabled={saving || isCompressing || !microchipValidated}
                    >
                      {photoPreviews.length > 0 ? 'Adaugă Mai Multe Poze' : 'Adaugă Poze'}
                      <input
                        type="file"
                        hidden
                        multiple
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handlePhotoUpload}
                      />
                    </Button>

                  </Stack>
                </Box>
              </Box>

              {/* Name field */}
              <Box>
                <TextField
                  fullWidth
                  required
                  name="name"
                  label="Nume"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={!!formErrors.name}
                  helperText={formErrors.name || 'Numele animalului'}
                  disabled={saving || !microchipValidated}
                />
              </Box>

              {/* Species select */}
              <Box>
                <FormControl fullWidth required error={!!formErrors.species}>
                  <InputLabel>Specie</InputLabel>
                  <Select
                    name="species"
                    value={formData.species}
                    onChange={(e) => handleInputChange('species', e.target.value)}
                    label="Specie"
                    disabled={saving || !microchipValidated}
                  >
                    {SPECIES.map((species) => (
                      <MenuItem key={species.value} value={species.value}>
                        {species.icon} {species.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formErrors.species || 'Selectează specia animalului'}
                  </FormHelperText>
                </FormControl>
              </Box>

              {/* Breed autocomplete */}
              <Box>
                <Autocomplete
                  options={availableBreeds}
                  getOptionLabel={(option) => option.label}
                  value={availableBreeds.find((b) => b.value === formData.breed) || null}
                  onChange={(_, newValue) => {
                    handleInputChange('breed', newValue?.value || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Rasă"
                      error={!!formErrors.breed}
                      helperText={formErrors.breed || 'Selectează sau caută rasa'}
                      disabled={saving || !formData.species || !microchipValidated}
                    />
                  )}
                  disabled={saving || !formData.species || !microchipValidated}
                  noOptionsText="Nu s-au găsit rase"
                />
              </Box>

              {/* Age */}
              <Box>
                <TextField
                  fullWidth
                  required
                  name="age_months"
                  label="Vârsta (luni)"
                  type="number"
                  value={formData.age_months}
                  onChange={(e) => handleInputChange('age_months', parseInt(e.target.value) || 0)}
                  error={!!formErrors.age_months}
                  helperText={formErrors.age_months || 'Vârsta în luni'}
                  inputProps={{ min: 0, max: 300 }}
                  disabled={saving || !microchipValidated}
                />
              </Box>

              {/* Gender */}
              <Box>
                <FormControl fullWidth required error={!!formErrors.gender}>
                  <InputLabel>Gen</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    label="Gen"
                    disabled={saving || !microchipValidated}
                  >
                    {GENDER_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formErrors.gender || 'Selectează genul animalului'}
                  </FormHelperText>
                </FormControl>
              </Box>

              {/* Size */}
              <Box>
                <FormControl fullWidth required error={!!formErrors.size}>
                  <InputLabel>Mărime</InputLabel>
                  <Select
                    name="size"
                    value={formData.size}
                    onChange={(e) => handleInputChange('size', e.target.value)}
                    label="Mărime"
                    disabled={saving || !microchipValidated}
                  >
                    {SIZE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formErrors.size || 'Selectează mărimea animalului'}
                  </FormHelperText>
                </FormControl>
              </Box>

              {/* Color */}
              <Box>
                <TextField
                  fullWidth
                  name="color"
                  label="Culoare"
                  value={formData.color}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  error={!!formErrors.color}
                  helperText={formErrors.color || 'Culoarea blănii/penajului'}
                  disabled={saving || !microchipValidated}
                />
              </Box>

              {/* Status */}
              <Box>
                <FormControl fullWidth required error={!!formErrors.status}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    label="Status"
                    disabled={saving || !microchipValidated}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formErrors.status || 'Selectează statusul animalului'}
                  </FormHelperText>
                </FormControl>
              </Box>

              {/* Description */}
              <Box>
                <TextField
                  fullWidth
                  name="description"
                  label="Descriere"
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Descrie personalitatea și caracteristicile animalului..."
                  disabled={saving || !microchipValidated}
                />
              </Box>

              {/* Special needs */}
              <Box>
                <TextField
                  fullWidth
                  name="special_needs"
                  label="Necesități speciale"
                  multiline
                  rows={2}
                  value={formData.special_needs}
                  onChange={(e) => handleInputChange('special_needs', e.target.value)}
                  placeholder="Dacă animalul are nevoie de îngrijire specială..."
                  disabled={saving || !microchipValidated}
                />
              </Box>

              {/* Medical info section */}
              <Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Informații medicale
                </Typography>
                
                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.microchipped}
                        onChange={(e) => handleInputChange('microchipped', e.target.checked)}
                        disabled={saving || !microchipValidated}
                      />
                    }
                    label="Microcipat"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.vaccinated}
                        onChange={(e) => handleInputChange('vaccinated', e.target.checked)}
                        disabled={saving || !microchipValidated}
                      />
                    }
                    label="Vaccinat"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.sterilized}
                        onChange={(e) => handleInputChange('sterilized', e.target.checked)}
                        disabled={saving || !microchipValidated}
                      />
                    }
                    label="Sterilizat"
                  />
                </Stack>
              </Box>

              {/* Compatibility */}
              <Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Compatibilitate
                </Typography>
                
                <Autocomplete
                  multiple
                  options={COMPATIBILITY_OPTIONS}
                  value={formData.compatibility}
                  onChange={(_, value) => handleInputChange('compatibility', value)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        key={option}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Compatibil cu"
                      placeholder="Selectează opțiunile..."
                      disabled={saving || !microchipValidated}
                    />
                  )}
                />
              </Box>
              </Box> {/* End of disabled fields section */}
            </Box>
          </CardContent>
        </Card>

        {/* Buttons */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/shelter/animals')}
            disabled={saving}
          >
            Anulează
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving || !formData.name || !microchipValidated}
          >
            {saving ? 'Se salvează...' : 'Salvează animalul'}
          </Button>
        </Box>
      </form>
    </Container>
  );
};

export default ShelterAnimalForm;
